import React from 'react';
import Link from 'next/link';
import styled, { css } from 'styled-components';
import styledContainerQuery from 'styled-container-query';
import tinycolor from 'tinycolor2';

import { DposLoyaltyDefault as DposLoyaltyIcon } from 'src/assets/dpos-loyalty-default';
import usePaths from 'src/hooks/use-paths';
import type { DutchiePosLoyaltyConfig, RewardsListV2 } from 'src/gql/graphql';
import { FooterContainer } from 'src/components/loyalty/loyalty-pass/footer.styles';
import { LoyaltyPassFooter } from 'src/components/loyalty/loyalty-pass/footer';
import { useLoyalty } from '../loyalty-tiers/use-loyalty';

type LoyaltyProps = {
  isWidgetLink?: boolean;
  locationMargin?: string;
  displayCTA?: boolean;
  activeEnterpriseId?: string;
  loyaltyEnterpriseConfigData?: DutchiePosLoyaltyConfig;
  rewardsEnterpriseData?: RewardsListV2;
};

export const CashRedemption = ({
  isWidgetLink = true,
  locationMargin = '',
  displayCTA = false,
  activeEnterpriseId = undefined,
  loyaltyEnterpriseConfigData = undefined,
  rewardsEnterpriseData = undefined,
}: LoyaltyProps): JSX.Element | null => {
  const {
    formatValueForDisplay,
    formatExpirationDate,
    rewardsProgramName,
    balanceDaysToExpiration,
    currentBalance,
    formatValueForDaysForExpire,
    getExpiresVerb,
    getExpiryUnit,
    loyaltyWidgetEnabled,
    soonestExpiringBalance,
    soonestExpirationDate,
    isDiscountRedemption,
  } = useLoyalty(activeEnterpriseId, loyaltyEnterpriseConfigData, rewardsEnterpriseData);
  const { href: baseHref } = usePaths();

  if (!loyaltyWidgetEnabled) {
    return null;
  }

  const url = `${baseHref}/user/loyalty`;
  const showSoonestExpiry = soonestExpiringBalance != null && !!soonestExpirationDate;

  return (
    <Wrapper
      href={isWidgetLink ? url : null}
      as={isWidgetLink ? Link : 'div'}
      $locationMargin={locationMargin}
      $displayCTA={displayCTA}
    >
      <DispoInfo>
        <Image>
          <DposLoyaltyIcon />
        </Image>

        <div>
          <Name>{rewardsProgramName}</Name>
        </div>
      </DispoInfo>
      <RewardHeaderText $displayCTA={displayCTA}>
        {formatValueForDisplay(currentBalance, 'cash', false)}
        <RewardHeaderSubtext $displayCTA={displayCTA}> rewards cash</RewardHeaderSubtext>
        {showSoonestExpiry && (
          <RewardExpire $displayCTA={displayCTA}>
            {formatValueForDisplay(soonestExpiringBalance, getExpiryUnit(soonestExpiringBalance), isDiscountRedemption)}{' '}
            {getExpiresVerb(soonestExpiringBalance)} {formatExpirationDate(soonestExpirationDate)}
          </RewardExpire>
        )}
        {!showSoonestExpiry && currentBalance > 0 && (
          <RewardExpire $displayCTA={displayCTA}>
            {formatValueForDaysForExpire(balanceDaysToExpiration ?? 0)}
          </RewardExpire>
        )}
      </RewardHeaderText>
      {displayCTA ? (
        <RewardCTAContainer>
          <DiscountCTA $displayCTA={displayCTA}>Earn cash back on every purchase</DiscountCTA>
        </RewardCTAContainer>
      ) : null}

      <LoyaltyPassFooter
        borderOverrides={css`
          left: -20px;
          right: -20px;
        `}
      />
    </Wrapper>
  );
};

const DispoInfo = styled.div`
  align-items: center;
  display: flex;
  gap: 16px;
`;

const Image = styled.div`
  border-radius: 50%;
  color: var(--dct--customized-colors-buttonsLinks);
  display: block;
  height: 56px;
  overflow: hidden;
  width: 56px;
`;

const Name = styled.div`
  font-size: 20px;
  font-weight: 700;
  line-height: ${18 / 16};
`;

const RewardCTAContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: flex-end;
`;

const RewardContainerHeader = styled.div`
  display: flex;
  width: 160px;
`;

const RewardHeaderText = styled.div`
  font-size: 24px;
  font-weight: 700;
  line-height: 1;
  width: 200px;
  display: block;
`;

const RewardHeaderSubtext = styled.div`
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  display: inline;
  margin-left: 8px;
`;

const DiscountCTA = styled.div`
  align-items: center;
  width: auto;
  background: ${({ theme }) => tinycolor.mix(theme.customized.colors.buttonsLinks, '#000000', 50).toString()};
  border-radius: 8px;
  text-align-last: center;
  justify-content: center;
  line-height: 1;
  padding: 16px 12px;
`;

const RewardExpire = styled.div<{ $displayCTA?: boolean }>`
  font-size: 12px;
  font-weight: 400;
  text-align: ${({ $displayCTA }) => ($displayCTA ? 'end' : 'start')};
  margin-top: 4px;
  display: block;
  line-height: 18px;
  margin-left: 8px;
`;

// This is our container query component
const Wrapper = styledContainerQuery.a`
  background: ${({ theme }) => theme.customized.colors.buttonsLinks};
  border-radius: ${({ theme }) => theme.customized.radius.lg};
  color: #fff;
  display: block;
  margin: ${({ $locationMargin }) => $locationMargin || '20px auto 40px'};
  max-width: 1200px;
  overflow: hidden;
  padding: 20px;
  position: relative;
  width: 100%;
  z-index: 1;

  /* In block mode, add margin-top to footer for spacing */
  ${FooterContainer} {
    margin-top: 20px;
  }

  &::before {
    background: #ffffff1a;
    border-radius: 50%;
    content: '';
    height: 400px;
    left: 48px;
    pointer-events: none;
    position: absolute;
    top: 48px;
    transform: translate(-50%, -50%);
    width: 400px;
    z-index: -1;
  }
  &:container(min-width: 250px and max-width: 599px) {
    ${RewardContainerHeader} {
      display: flex;
      width: ${({ $displayCTA }) => ($displayCTA ? '160px !important' : '100%')};
      justify-content: center;
    }

    ${RewardHeaderText} {
        font-size: 24px;
        font-weight: 700;
        line-height: 1;
        width: 100%;
        margin-top: 24px;
        margin-bottom: ${({ $displayCTA }) => ($displayCTA ? '20px' : '0')};
    }
    ${RewardExpire} {
      font-size: 12px;
      font-weight: 400;
      text-align: start;
      line-height: 18px;
      float: right;
      margin-top: 8px;
      display: ${({ $displayCTA }) => ($displayCTA ? 'inline' : 'block')};
      width: ${({ $displayCTA }) => ($displayCTA ? 'auto' : 'auto')};
    }

    ${RewardCTAContainer} {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
      justify-content: space-between;
      margin: 0;
    }
    ${RewardHeaderSubtext} {
      margin-top:5px;
    }
    ${DiscountCTA} {
      width: 100%;
    }
  }
  @media only screen and (min-width: 600px) and (max-width: 899px) {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;

    /* In flex mode, remove margin-top from footer */
    ${FooterContainer} {
      margin-top: 0;
    }

    ${RewardCTAContainer} {
      align-items: center;
      gap: 16px;
      margin: 0;
      order: 3;
      width: 100%;
    }
    ${RewardExpire} {
      font-size: 12px;
      font-weight: 400;
      text-align: start;
      margin-top: 8px;
      display: inline;
      line-height: 18px;
      float: right;
      width: auto;
    }
    ${RewardHeaderText} {
      width: ${({ $displayCTA }) => ($displayCTA ? '400px' : 'auto')};
    }
    ${DiscountCTA} {
      width: 400px;
    }
  }
  @media only screen and (min-width: 900px) {
    justify-content: space-between;
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 20px;

    /* In flex mode, remove margin-top from footer */
    ${FooterContainer} {
      margin-top: 0;
    }

    ${DispoInfo} {
      max-width: 218px;
    }
    ${RewardCTAContainer} {
      flex: 1;
      order: unset;
      width: auto;
    }
    ${RewardExpire} {
      font-size: 12px;
      font-weight: 400;
      text-align: start;
      margin-top: ${({ $displayCTA }) => ($displayCTA ? '8px' : '0')};
      display: ${({ $displayCTA }) => ($displayCTA ? 'inline' : 'block')};
      float: ${({ $displayCTA }) => ($displayCTA ? 'right' : 'none')};
      width: auto;
    }
    ${DiscountCTA} {
      justify-content: flex-end;
    }
    ${RewardHeaderText} {
      margin-top: 0;
      width: ${({ $displayCTA }) => ($displayCTA ? 'auto' : '200px')};
      display: ${({ $displayCTA }) => ($displayCTA ? 'flex' : 'block')};
      align-items: end;
    }
    ${RewardHeaderSubtext} {
       display: ${({ $displayCTA }) => ($displayCTA ? 'flex' : 'inline')};
       align-items: end;
    }
  }
`;
